import type { AddToCartArgs, RemoveFromCartArgs, UpdateCartItemQuantityArgs } from 'TYPES/api.body';
import { addTier1CartItem, revalidateTier1Cart } from 'network/services/goods';
import { findMadinaCartItemId, removeMadinaCartItem, updateMadinaCartItem } from 'network/services/madina';
import { getStoredBranchId } from 'utils/branch';
import {
  BUSINESS_ADD_TO_CART,
  BUSINESS_REMOVE_FROM_CART,
  LIKE_PRODUCT,
  REMOVE_FROM_CART,
  UPDATE_BUSINESS_CART_ITEM_QUANTITY,
  UPDATE_CART_ITEM_QUANTITY
} from '../config/endpoints';
import api from '../config/setAuthHeader';

export const addProductToCart = async (opts: AddToCartArgs) => {
  const branchId = getStoredBranchId();

  if (opts.customerType !== 'BUSINESS' && branchId) {
    const item = opts.items[0];
    if (!item) throw new Error('No product selected');
    await addTier1CartItem(item.product_id, item.quantity);
    return revalidateTier1Cart();
  }

  const urlParams = new URLSearchParams(window.location.search);
  const locationId = urlParams.get('locationId');

  let query = opts.items
    .map(item => `product_id=${item.product_id}&product_quantity=${item.quantity}`)
    .join('&');

  if (locationId) {
    query += `&location_id=${locationId}`;
  }

  const businessQuery = opts.items.map(item => `/${item.product_id}/?quantity=${item.quantity}`);

  if (opts.customerType === 'BUSINESS' && opts.items.length > 0) {
    const { data } = await api.post(BUSINESS_ADD_TO_CART + businessQuery);
    return data;
  }

  const { data } = await api.post(`/api/v1/customer/cart/add?${query}`);
  return data;
};

export const removeProductFromCart = async (opts: RemoveFromCartArgs) => {
  const branchId = getStoredBranchId();

  if (opts.customerType !== 'BUSINESS' && branchId) {
    const productId = Number(opts.id);
    if (!Number.isFinite(productId) || productId <= 0) {
      throw new Error('Invalid product id for cart removal');
    }
    const cartItemId = opts.cartItemId ?? (await findMadinaCartItemId(branchId, productId));
    return removeMadinaCartItem(branchId, cartItemId);
  }

  if (opts.customerType === 'BUSINESS') {
    const { data } = await api.delete(`${BUSINESS_REMOVE_FROM_CART}${opts.id}?order_id=${opts.orderId}`);
    return data;
  }

  const { data } = await api.delete(`${REMOVE_FROM_CART}${opts.id}?order_id=${opts.orderId}`);
  return data;
};

export const likeProduct = async (id: number) => {
  const { data } = await api.post(`${LIKE_PRODUCT}${id}/like-product`);
  return data;
};

export const unlikeProduct = async (id: number) => {
  const { data } = await api.delete(`${LIKE_PRODUCT}${id}/remove-from-favourite`);
  return data;
};

export const updateCartItemQty = async ({
  orderId,
  qty,
  productId,
  cartItemId,
  customerType
}: UpdateCartItemQuantityArgs) => {
  const branchId = getStoredBranchId();

  if (customerType !== 'BUSINESS' && branchId) {
    const resolvedCartItemId = cartItemId ?? (await findMadinaCartItemId(branchId, productId));
    return updateMadinaCartItem(resolvedCartItemId, { quantity: qty, branch_id: branchId });
  }

  if (customerType === 'BUSINESS') {
    const { data } = await api.put(`${UPDATE_BUSINESS_CART_ITEM_QUANTITY}${orderId}/edit-quantity?quantity=${qty}&productId=${productId}`);
    return data;
  }

  const { data } = await api.put(`${UPDATE_CART_ITEM_QUANTITY}${orderId}/edit-quantity?quantity=${qty}&productId=${productId}`);
  return data;
};
